import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Slide,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * App-styled confirmation dialog (replaces window.confirm).
 */
export default function ConfirmAlert({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmColor = "error",
  loading = false,
  icon: Icon = WarningAmberOutlined,
  maxWidth = "xs",
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const paperBg = theme.palette.background.default;

  const handleConfirm = () => {
    if (loading) return;
    onConfirm?.();
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Transition}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(12px) saturate(180%)",
            WebkitBackdropFilter: "blur(12px) saturate(180%)",
            backgroundColor: isDark ? alpha("#000000", 0.7) : alpha("#000000", 0.4),
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: isMobile ? 0 : 3,
          backgroundColor: isDark ? paperBg : theme.palette.background.paper,
          border: `1px solid ${isDark ? alpha("#ffffff", 0.1) : alpha("#000000", 0.08)}`,
          boxShadow: isDark
            ? `0 8px 40px ${alpha("#000000", 0.6)}`
            : `0 8px 32px ${alpha("#000000", 0.12)}`,
        },
      }}
    >
      <DialogContent sx={{ pt: 3, pb: 1 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          {Icon ? (
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette[confirmColor]?.main || theme.palette.warning.main, 0.12),
                color: theme.palette[confirmColor]?.main || theme.palette.warning.main,
              }}
            >
              <Icon sx={{ fontSize: 26 }} />
            </Box>
          ) : null}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {title}
            </Typography>
            {typeof message === "string" ? (
              <Alert severity="warning" sx={{ mt: 0.5 }}>
                {message}
              </Alert>
            ) : (
              message
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined" sx={{ fontWeight: 600 }}>
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          variant="contained"
          color={confirmColor}
          sx={{ fontWeight: 700, minWidth: 96 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
